import { useTranslation } from 'react-i18next';
import { useRef, useState, useEffect } from 'react';
import { motion, useInView } from 'framer-motion';
import ScrollReveal from '@/components/base/ScrollReveal';

const RESULTS = [
  { key: 'result1', icon: 'ri-eye-line', value: 85, suffix: '%' },
  { key: 'result2', icon: 'ri-user-add-line', value: 320, suffix: '+' },
  { key: 'result3', icon: 'ri-funds-line', value: 3.2, suffix: 'x' },
  { key: 'result4', icon: 'ri-seedling-line', value: 3, suffix: 'x' },
  { key: 'result5', icon: 'ri-shopping-bag-3-line', value: 40, suffix: '%' },
  { key: 'result6', icon: 'ri-robot-2-line', value: 100, suffix: '%' },
  { key: 'result7', icon: 'ri-line-chart-line', value: 12, suffix: 'x' },
];

function ResultCard({ item, index }: { item: typeof RESULTS[0]; index: number }) {
  const ref = useRef<HTMLDivElement>(null);
  const isInView = useInView(ref, { once: true, margin: '-50px 0px -50px 0px', amount: 0.4 });
  const [count, setCount] = useState(0);
  const { t } = useTranslation();

  useEffect(() => {
    if (!isInView) return;
    const duration = 2000;
    const steps = 40;
    const increment = item.value / steps;
    let current = 0;
    const timer = setInterval(() => {
      current += increment;
      if (current >= item.value) {
        setCount(item.value);
        clearInterval(timer);
      } else {
        setCount(Math.floor(current * 10) / 10);
      }
    }, duration / steps);
    return () => clearInterval(timer);
  }, [isInView, item.value]);

  const displayValue = Number.isInteger(count) ? count.toLocaleString() : count.toFixed(1);

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 30 }}
      animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
      transition={{ duration: 0.6, delay: index * 0.08, ease: [0.22, 1, 0.36, 1] }}
      className="bg-white rounded-2xl p-6 border border-gray-100 shadow-[0_2px_16px_rgba(0,0,0,0.03)] hover:shadow-[0_8px_40px_rgba(0,0,0,0.06)] transition-all duration-400 flex flex-col items-center text-center cursor-pointer"
    >
      <div className="w-12 h-12 rounded-xl bg-[#E53935]/5 flex items-center justify-center mb-4">
        <i className={`${item.icon} text-[#E53935] text-xl`}></i>
      </div>
      <div className="font-heading text-3xl md:text-4xl font-extrabold text-[#111111] mb-1">
        {displayValue}{item.suffix}
      </div>
      <span className="text-gray-600 text-sm font-medium">
        {t(`trafficMaster.${item.key}`)}
      </span>
    </motion.div>
  );
}

export default function Results() {
  const { t } = useTranslation();

  return (
    <section className="py-20 md:py-32 bg-white">
      <div className="max-w-6xl mx-auto px-6 md:px-10">
        <ScrollReveal>
          <div className="text-center mb-16">
            <span className="inline-block px-4 py-1.5 rounded-full bg-[#F7F8FA] text-[#C8A24A] text-xs font-semibold tracking-wider uppercase mb-5 border border-gray-100">
              {t('trafficMaster.resultsTag')}
            </span>
            <h2 className="font-heading text-3xl md:text-5xl font-bold text-[#111111] leading-tight">
              {t('trafficMaster.resultsTitle')}
            </h2>
          </div>
        </ScrollReveal>

        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-7 gap-4">
          {RESULTS.map((item, i) => (
            <ResultCard key={item.key} item={item} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
}